<template>
  <div class="flex-1">
    <!-- Search Bar -->
    <div class="mc-search-container">
      <div class="mc-search-input">
        <input :value="props.searchQuery" type="text" placeholder="Search Icons"
          class="w-full bg-transparent outline-none p-2"
          @input="(event) => { emit('update:searchQuery', event.target.value); hideTooltip(); }">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6 mr-2 cursor-pointer text-gray-300" @click="emit('update:searchQuery', '')">
          <title>Clear</title>
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
      </div>
      
      <div class="banner-container">
        <a :href="mciconsUrl" class="h-full w-full flex justify-center sm:justify-end" target="_blank" @click="handleBannerClick">
          <img :src="codeBanner" alt="Vault Banner1" class="promo-banner">
        </a>
      </div>
    </div>

    <!-- Category Pills display -->
    <div v-if="hasSubcategories && props.selectedCategory !== 'All'" class="mc-category-selector">
      <div class="mc-category-container">
        <!-- Toggle Button - Positioned Independently -->
        <button
          v-if="subcategories.length > displayedSubcategories.length || isExpanded"
          @click="toggleSubcategories"
          class="mc-category-toggle-arrow"
        >
          <span class="arrow-icon">{{ isExpanded ? '▲' : '▼' }}</span>
        </button>
        
        <transition-group
          name="slide-fade"
          class="mc-category-list"
          :style="{ maxHeight: isExpanded ? '1000px' : '40px' }"
        >
          <button
            v-for="sub in displayedSubcategories"
            :key="sub.name"
            @click="selectSubcategory(sub.name)"
            class="mc-category-button"
            :class="{ 
              'mc-category-active': selectedSubcategory === sub.name, 
              'mc-category-inactive': selectedSubcategory !== sub.name 
            }"
          >
            <span>{{ sub.name === 'All' ? 'All' : formatCategoryName(sub.name) }}</span>
            <span class="mc-category-count">{{ sub.fileCount }}</span>
          </button>
        </transition-group>
      </div>
    </div>

    <!-- No Results Message -->
    <div v-if="props.searchQuery && filteredIcons.length === 0" class="mc-no-results">
      No assets found matching "{{ props.searchQuery.slice(0, 10) + (props.searchQuery.length > 10 ? '...' : '') }}"
    </div>

    <!-- Icons Grid -->
    <transition name="fade" mode="out-in">
      <div v-if="!props.searchQuery || filteredIcons.length > 0" class="mc-grid-container">
        <div v-if="icons.length === 0" class="flex justify-center py-4">
          <img src="@/assets/loading.gif" alt="Loading..." class="w-16 h-16">
        </div>
        <div v-else :class="gridClass" class="mc-icons-grid">
          <template v-for="(icon, index) in paginatedIcons" :key="icon.name + icon.category">
            <div
              class="mc-icon-item mc-icon-animate"
              :style="{ animationDelay: Math.min(index * 15, 600) + 'ms' }"
              :class="{ 'mc-icon-special': icon.name.split('/').pop().includes('$') }" 
              @mouseenter="showTooltip(icon, $event)" 
              @mouseleave="hideTooltip"
              @click="(event) => {
                event.shiftKey ? downloadIconHandler(icon) : openModal(icon);
                hideTooltip();
              }">
                <img 
                v-if="icon.thumbnailUrl" 
                v-lazy="icon.thumbnailUrl" 
                :alt="icon.name" 
                class="mc-icon-image">
            </div>
          </template>
        </div>
      </div>
    </transition>

    <!-- Tooltip -->
    <div v-if="tooltip.visible" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }" class="mc-tooltip">
      <div class="mc-tooltip-title">{{ tooltip.text }}</div>
      <div class="mc-tooltip-category">{{ tooltip.category }}</div>
    </div>

    <!-- Modal -->
    <Transition name="modal">
      <div v-if="modal.visible" class="fixed inset-0 flex items-center justify-center z-50">
        <div class="absolute inset-0 bg-black bg-opacity-70" @click="closeModal"></div>
        <div class="mc-modal">
          <!-- Close button -->
          <button @click="closeModal" class="mc-modal-close">×</button>
          
          <!-- Modal content -->
          <div class="mc-modal-content">
            <div class="mc-modal-image-container">
              <img 
                v-if="modal.icon?.loading" 
                src="@/assets/loading.gif" 
                alt="Loading..." 
                class="object-contain">
              <img 
                v-else 
                :src="modal.icon?.url" 
                :alt="modal.icon?.name" 
                @load="getImageDimensions"
                :style="getModalImageStyle"
                class="mc-modal-image">
            </div>
            
            <div class="mc-modal-info">
              <div class="mc-modal-title">{{ formatIconName(modal.icon?.name) }}</div>
              <div class="mc-modal-tags">
                <span v-if="modal.icon?.subcategory" class="mc-tag">
                  {{ formatCategoryName(modal.icon.subcategory) }}
                </span>
                <span class="mc-tag">
                  {{ formatCategoryName(modal.icon?.category) }}
                </span>
                <span v-if="imageDimensions" class="mc-tag">
                  {{ imageDimensions.width }}×{{ imageDimensions.height }}
                </span>
              </div>
            </div>

            <button 
              @click="downloadIconHandler(modal.icon)"
              class="mc-download-button">
              Download
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mc-pagination">
      <button 
        @click="changePage(currentPage - 1)" 
        :disabled="currentPage === 1" 
        class="mc-page-button mc-page-nav"
        :class="{ 'mc-page-disabled': currentPage === 1 }"
      >
        &lt;
      </button>

      <template v-for="(item, index) in displayedPageNumbers" :key="index">
        <button 
          v-if="item !== '...'" 
          @click="changePage(item)"
          class="mc-page-button"
          :class="{ 'mc-page-active': currentPage === item }"
        >
          {{ item }}
        </button>
        <div v-else class="mc-page-ellipsis">...</div>
      </template>

      <button 
        @click="changePage(currentPage + 1)" 
        :disabled="currentPage === totalPages" 
        class="mc-page-button mc-page-nav"
        :class="{ 'mc-page-disabled': currentPage === totalPages }"
      >
        &gt;
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed, getCurrentInstance, onUnmounted } from 'vue';
import { authenticatedGet } from '../utils/auth';
import codeBanner from '@/assets/banners/code-banner.png';
import './gridStyles.css';

const mciconsUrl = getCurrentInstance().appContext.config.globalProperties.$mciconsUrl;

const handleBannerClick = (event) => {
  if (Math.random() < 0.5) {
    event.preventDefault();
    window.open('https://app.ccleaf.com?ref=apollo', '_blank');
  }
};

// States
const icons = ref([]);
const subcategories = ref([]);
const selectedSubcategory = ref('All');
const isExpanded = ref(false);
const currentPage = ref(1);
const itemsPerPage = 180; // Adjusted to match the image grid
const tooltip = ref({
  visible: false,
  text: '',
  category: '',
  x: 0,
  y: 0
});
const modal = ref({
  visible: false,
  icon: null,
  loading: true
});
const imageDimensions = ref(null);

// Helper function declarations
const calculateInitialSubcategoryCount = () => {
  if (!subcategories.value.length) return 0;
  
  const containerWidth = window.innerWidth - 100;
  let currentLineWidth = 0;
  let count = 0;
  
  for (const sub of subcategories.value) {
    const estimatedWidth = (sub.name.length * 8) + 60;
    
    if (currentLineWidth + estimatedWidth > containerWidth) {
      break;
    }
    
    currentLineWidth += estimatedWidth + 12;
    count++;
  }
  
  return Math.max(1, count - 1);
};

const initialSubcategoryCount = ref(calculateInitialSubcategoryCount());

const props = defineProps({
  selectedCategory: String,
  searchQuery: String,
  resetTrigger: Number
});

const emit = defineEmits(['update:searchQuery']);

const hasSubcategories = computed(() => {
  return subcategories.value.length > 1; // More than just "All" category
});

const fetchIcons = async (category) => {
  try {
    const endpoint = category === 'All' ? '/api/assets/all' : `/api/assets/${category}`;
    
    const response = await authenticatedGet(endpoint);

    if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
      console.error('Received HTML response instead of JSON');
      throw new Error('Invalid response format');
    }

    if (Array.isArray(response.data)) {
      if (category === 'All') {
        // Handle 'All' category response
        const [, filesData] = response.data;
        
        // Set empty subcategories for 'All' view
        subcategories.value = [];

        // Map files with their categories and subcategories
        icons.value = filesData.files.map(file => ({
          url: file.subcategory 
            ? `/assets/${file.category}/${file.subcategory}/${file.file}`
            : `/assets/${file.category}/${file.file}`,
          thumbnailUrl: file.subcategory 
            ? `/thumbnails/${file.category}/${file.subcategory}/${file.file}`
            : `/thumbnails/${file.category}/${file.file}`,
          name: file.file,
          category: file.category,
          subcategory: file.subcategory
        }));
      } else {
        const [data] = response.data;
        
        // Reset subcategories and selected subcategory
        subcategories.value = [];
        selectedSubcategory.value = 'All';

        // Map the main category files (might be empty)
        const mainFiles = data.files.map(file => ({
          url: `/assets/${category}/${file.file}`,
          thumbnailUrl: `/thumbnails/${category}/${file.file}`,
          name: file.file,
          category,
          subcategory: null
        }));

        // If there are subcategories, set them up
        if (data.subcategories && data.subcategories.length > 0) {
          // Get all files from subcategories
          const subcategoryFiles = data.subcategories.flatMap(sub => 
            sub.files.map(file => ({
              url: `/assets/${category}/${sub.name}/${file}`,
              thumbnailUrl: `/thumbnails/${category}/${sub.name}/${file}`,
              name: file,
              category,
              subcategory: sub.name
            }))
          );

          // Calculate total files
          const totalFiles = mainFiles.length + subcategoryFiles.length;
          
          if (totalFiles > 0) {
            const allSubcategory = {
              name: 'All',
              fileCount: totalFiles,
              files: [...mainFiles, ...subcategoryFiles]
            };
            
            const mappedSubcategories = data.subcategories
              .filter(sub => sub.files.length > 0)
              .sort((a, b) => {
                const numA = parseInt(a.name.split('.')[0]);
                const numB = parseInt(b.name.split('.')[0]);
                return numA - numB;
              })
              .map(sub => ({
                name: sub.name,
                fileCount: sub.files.length,
                files: sub.files.map(file => ({
                  url: `/assets/${category}/${sub.name}/${file}`,
                  thumbnailUrl: `/thumbnails/${category}/${sub.name}/${file}`,
                  name: file,
                  category,
                  subcategory: sub.name
                }))
              }));

            subcategories.value = [allSubcategory, ...mappedSubcategories];
            // Set icons to all files when initially loading
            icons.value = allSubcategory.files;
          }
        } else {
          icons.value = mainFiles;
        }
      }
    }
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      await fetchIcons(category);
      return;
    }
    console.error('Error fetching icons:', error);
  }
};

// Continue with the filteredIcons computed property
const filteredIcons = computed(() => {
  const searchTerm = props.searchQuery?.toLowerCase().trim().replace(/\s+/g, '_') || '';
  let allIcons;

  const uniqueFiles = new Map();
  
  if (hasSubcategories.value) {
    const relevantFiles = selectedSubcategory.value === 'All'
      ? subcategories.value.flatMap(sub => sub.files)
      : subcategories.value.find(sub => sub.name === selectedSubcategory.value)?.files || [];

    relevantFiles.forEach(file => {
      // Use full path as unique identifier
      const key = `${file.category}/${file.subcategory || ''}/${file.name}`;
      uniqueFiles.set(key, file);
    });
    
    allIcons = Array.from(uniqueFiles.values());
  } else {
    // For non-subcategory views, use full path as key
    icons.value.forEach(file => {
      const key = `${file.category}/${file.subcategory || ''}/${file.name}`;
      uniqueFiles.set(key, file);
    });
    allIcons = Array.from(uniqueFiles.values());
  }

  // Filter by search term
  const filteredIcons = allIcons.filter(icon => 
    icon.name.toLowerCase().includes(searchTerm)
  );

  // If we're in the "All" category view, sort by category first
  if (props.selectedCategory === 'All') {
    // Group by category
    const groupedIcons = filteredIcons.reduce((acc, icon) => {
      if (!acc[icon.category]) {
        acc[icon.category] = [];
      }
      acc[icon.category].push(icon);
      return acc;
    }, {});

    // Sort each category's icons alphabetically and flatten
    return Object.entries(groupedIcons)
      .sort(([catA], [catB]) => {
        // Extract numbers from category names for proper numerical sorting
        const numA = parseInt(catA.match(/^\d+/)?.[0] || '0');
        const numB = parseInt(catB.match(/^\d+/)?.[0] || '0');
        return numA - numB;
      })
      .flatMap(([, icons]) => 
        icons.sort((a, b) => {
          const nameA = a.name.replace(/\.(png|webp|gif)$/, '').toLowerCase();
          const nameB = b.name.replace(/\.(png|webp|gif)$/, '').toLowerCase();
          return nameA.localeCompare(nameB);
        })
      );
  }

  // For other views, just sort alphabetically
  return filteredIcons.sort((a, b) => {
    const nameA = a.name.replace(/\.(png|webp|gif)$/, '').toLowerCase();
    const nameB = b.name.replace(/\.(png|webp|gif)$/, '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
});

const paginatedIcons = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredIcons.value.slice(start, end);
});

const totalPages = computed(() => {
  return Math.ceil(filteredIcons.value.length / itemsPerPage);
});

const changePage = (page) => {
  currentPage.value = page;
};

const selectSubcategory = (name) => {
  selectedSubcategory.value = name;
  currentPage.value = 1; // Reset page when subcategory changes
};

watch(() => props.selectedCategory, (newCategory) => {
  icons.value = [];
  subcategories.value = [];
  currentPage.value = 1; // Reset page when category changes
  isExpanded.value = false; // Reset expanded state when category changes
  fetchIcons(newCategory);
});

watch(() => props.searchQuery, () => {
  currentPage.value = 1; // Reset page when search query changes
});

// Function to reset subcategories state
const resetSubcategoriesState = () => {
  subcategories.value = [];
  selectedSubcategory.value = 'All';
  isExpanded.value = false;
};

onMounted(() => {
  fetchIcons(props.selectedCategory);
  window.addEventListener('resize', () => {
    initialSubcategoryCount.value = calculateInitialSubcategoryCount();
  });

  // Listen to tab changes via URL
  window.addEventListener('popstate', resetSubcategoriesState);
});

onUnmounted(() => {
  // Clean up event listeners
  window.removeEventListener('popstate', resetSubcategoriesState);
  window.removeEventListener('resize', () => {});
});

// Watch for tab changes
watch(() => window.location.pathname, (newPath, oldPath) => {
  // If we come back to icons tab from another tab, reset state
  if ((newPath === '/' || newPath === '') && oldPath && oldPath !== newPath) {
    resetSubcategoriesState();
    fetchIcons(props.selectedCategory);
  }
}, { immediate: true });

// Function to format the icon name for display
const formatIconName = (name) => {
  if (!name) return '';
  // Get just the filename without the path
  const filename = name.split('/').pop();
  // Remove extension and '$' characters, then format
  return filename
    .replace(/\.(png|jpg|jpeg|webp|gif|bmp|svg|ico|tiff)$/, '') // Remove extension
    .replace(/\$/g, '') // Remove $ characters for display
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
};

// Function to format the category name by removing numbering
const formatCategoryName = (category) => {
  if (!category) return ''; // Add safety check for null/undefined
  return category.toString().replace(/^[^\s]*\s*/, '');
};

// Compute grid class based on selected category
const gridClass = computed(() => {
  // Always use standard grid (9 items) for the "All" category
  if (props.selectedCategory === 'All') {
    return 'mc-grid-standard';
  }
  
  // Check if the selected category or subcategory includes '$'
  const isCategorySpecial = props.selectedCategory.includes('$');
  const isSubcategorySpecial = selectedSubcategory.value.includes('$');
  
  // Check if the current category from the API data contains $
  const hasSpecialCategory = icons.value.some(icon => 
    icon.category && icon.category.includes('$')
  );

  // Adjust the grid class based on the conditions
  return (isCategorySpecial || isSubcategorySpecial || hasSpecialCategory) 
    ? 'mc-grid-large' 
    : 'mc-grid-standard';
});

// Tooltip positioning
const showTooltip = (icon, event) => {
  const iconElement = event.currentTarget;
  const rect = iconElement.getBoundingClientRect();

  tooltip.value.text = formatIconName(icon.name);
  
  // Format category display
  const categoryText = formatCategoryName(icon.category);
  const subcategoriesText = icon.subcategories?.length 
    ? ` > ${icon.subcategories.map(sub => formatCategoryName(sub)).join(', ')}`
    : icon.subcategory 
      ? ` > ${formatCategoryName(icon.subcategory)}`
      : '';
  
  tooltip.value.category = categoryText + subcategoriesText;
  tooltip.value.x = rect.right + 5;
  tooltip.value.y = rect.top + (rect.height / 2);
  tooltip.value.visible = true;
};

const hideTooltip = () => {
  tooltip.value.visible = false;
};

const openModal = (icon) => {
  modal.value.icon = icon;
  modal.value.loading = true;
  modal.value.visible = true;
};

const closeModal = () => {
  modal.value.visible = false;
};

const getImageDimensions = (event) => {
  imageDimensions.value = {
    width: event.target.naturalWidth,
    height: event.target.naturalHeight
  };
  modal.value.loading = false;
};

const getModalImageStyle = computed(() => {
  if (!imageDimensions.value) return {};
  
  const { width, height } = imageDimensions.value;
  const isWidthSmaller = width < height;
  
  // If image width is smaller than height, limit height to 600px and let width adjust
  // If image height is smaller than width, limit width to 600px and let height adjust
  // Always maintain aspect ratio by setting both width and height to 100%
  return {
    maxWidth: isWidthSmaller ? 'none' : '600px',
    maxHeight: isWidthSmaller ? '600px' : 'none',
    width: '100%',
    height: '100%'
  };
});

const downloadIconHandler = (icon) => {
  const link = document.createElement('a');
  link.href = icon.url; 
  link.download = icon.name; 
  document.body.appendChild(link); 
  link.click(); 
  document.body.removeChild(link); 
};

const displayedSubcategories = computed(() => {
  if (!subcategories.value.length) return [];
  
  // Always include "All" category first
  const result = subcategories.value.slice(0, 1);
  
  if (isExpanded.value) {
    return subcategories.value;
  }
  
  // Add categories that fit in the first line
  let currentWidth = result[0] ? (result[0].name.length * 8) + 60 : 0;
  const containerWidth = window.innerWidth - 150; 
  
  for (let i = 1; i < subcategories.value.length; i++) {
    const sub = subcategories.value[i];
    const estimatedWidth = (sub.name.length * 8) + 60;
    
    if (currentWidth + estimatedWidth > containerWidth) {
      break;
    }
    
    result.push(sub);
    currentWidth += estimatedWidth + 12;
  }
  
  return result;
});

const toggleSubcategories = () => {
  isExpanded.value = !isExpanded.value;
};

const displayedPageNumbers = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  
  if (total <= 7) {
    // If we have 7 or fewer pages, show all pages without ellipsis
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  
  // We need to display ellipsis
  if (current <= 3) {
    // We're near the beginning: show 1 2 3 4 5 ... lastPage
    return [1, 2, 3, 4, 5, '...', total];
  } else if (current >= total - 2) {
    // We're near the end: show 1 ... lastPage-4 lastPage-3 lastPage-2 lastPage-1 lastPage
    return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  } else {
    // We're in the middle: show 1 ... current-1 current current+1 ... lastPage
    return [1, '...', current - 1, current, current + 1, '...', total];
  }
});

// Watch for reset trigger changes
watch(() => props.resetTrigger, () => {
  if (props.resetTrigger > 0) {
    resetSubcategoriesState();
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.mc-category-toggle-arrow {
  position: absolute;
  top: 0;
  right: 0;
  height: 2rem;
  min-width: 2rem;
  background-color: #141414;
  border: 2px solid #00e5ff;
  border-radius: 10px;
  cursor: pointer;
  color: #00e5ff;
  transition: all 0.2s ease;
  padding: 3px 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.mc-category-toggle-arrow:hover {
  color: white;
}

.arrow-icon {
  font-size: 14px;
}

/* Ensure proper container positioning */
.mc-category-container {
  position: relative;
  width: 100%;
}

.mc-category-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-right: 30px; /* Make room for the arrow button */
}
</style>